import { useEffect, useState } from "react";
import { X, Check, ArrowRight } from "lucide-react";
import { categoryEmoji } from "@/lib/giftRequest";
import type { DashboardConciergeDrawerRecipient } from "@/components/concierge/DashboardConciergeDrawer";
import { useFloatingConciergeUi } from "@/contexts/FloatingConciergeContext";
import {
  PersonalizationHintsStrip,
  pickPersonalizationHints,
} from "@/components/concierge/PersonalizationHintsStrip";
import { useProfile, useTeamMembers } from "@/hooks/queries";

// The warm B-style approval modal that lifts in over the calm dashboard
// when the customer taps a celebration row. Renders the recommendation
// the engine has drafted (or, in this v1, the stub data passed in via
// props). Has three terminal actions, Send / Adjust / Skip, and a
// success overlay that animates in after Send.
//
// Data shape mirrors the celebration_recommendations table introduced
// in 20260511030000_celebration_recommendations.sql. Adjust + Skip are
// wired as no-op handlers for now; the parent passes them in so the
// real engine wiring slots in later.

export interface ApprovalRecommendation {
  recipientName: string;
  recipientFirstName: string;
  occasion: string; // e.g. "birthday", "3-year anniversary"
  date: string; // ISO date
  daysOut: number; // 4 → "4 days away"; 0 → "today"
  ageOrYears?: string; // e.g. "thirty-one" for birthdays, "third year" for anniversaries
  bakery: string;
  // imageUrl: hero photo of the actual product. v1 stub uses
  // representative Unsplash photos per tier; once the engine is
  // wired, this comes from bakery_products.image_url (which we'll
  // need to add, column not in the schema today).
  imageUrl?: string;
  imageAlt?: string;
  productSummary: string; // one-line what's coming
  productLine: string; // narrative paragraph
  cardDetail: string;
  deliveryDetail: string;
  rationaleDetail: string;
  totalCents: number;
  tier: "standard" | "premium" | "luxury";
  editWindowCloses: string; // human-readable
  // MS68: resolved vendor_category (cake / hamper / florist / spa /
  // workshop / experience / voucher / meal). Optional so existing
  // callers don't break; when present, drives the Bit emoji so the
  // modal renders an honest category icon instead of a cupcake.
  // Defaults to 🎁 gift when undefined.
  vendorCategory?: string;
  // MS68: lifted from the Dashboard's `recommendationFor` extension
  // shape into the public interface. Used by the modal footer to
  // gate the "AutoPilot ships as drafted" sentence — only true for
  // autopilot recs, misleading for manually-drafted/ready ones.
  statusKey?: "ready" | "drafting" | "autopilot" | "done" | "locked_in" | "skipped";
  // MS95: resolved vendor_products.id when the recommendation came
  // from the v2 engine (DB-backed). Null/undefined for v1 narrative-
  // only recs (Dashboard's current path). Carriers persist this to
  // celebration_recommendations.line_items so downstream SKU-aware
  // surfaces (image enrichment, RichSkuDetailSection,
  // MemberHistorySection) can resolve the picked SKU.
  skuId?: string | null;
}

interface ApprovalModalProps {
  open: boolean;
  recommendation: ApprovalRecommendation | null;
  onClose: () => void;
  onSend: (
    rec: ApprovalRecommendation,
  ) => void | Promise<void | { redirected: true } | { openedCheckout: true }>;
  onAdjust?: (rec: ApprovalRecommendation) => void;
  onSkip?: (rec: ApprovalRecommendation) => void;
  /** When the celebration was skipped — show Revitalize instead of Send/Adjust/Skip. */
  celebrationSkipped?: boolean;
  onRevitalize?: (rec: ApprovalRecommendation) => void | Promise<void>;
  // Guest mode: rendered for one-shot /order flow (no registered
  // celebration). Changes the success copy + adds a "Create an
  // account to do this on autopilot" bridge so the guest funnel
  // converts to registered concierge.
  guestMode?: boolean;
  onCreateAccount?: () => void;
  // MS76: intent-only mode. The live Dashboard approval flow saves
  // an approval intent (celebration_recommendations row) but DOES
  // NOT yet create an order or charge — the create-checkout-session
  // wiring is the next step. Until that lands, the success copy
  // "It's on its way" + "charged when you tap send" is a falsehood
  // for the live authed dashboard path. When intentOnly=true, we
  // render honest copy that matches what actually happened
  // ("Saved. We'll firm up timing and reach out before charging.").
  // Demo and guest modes ignore this flag and keep their optimistic
  // copy because the demo IS a prototype and the guest flow DOES
  // immediately checkout via Stripe.
  intentOnly?: boolean;
  // When true the celebration is already approved — Send is disabled and
  // the modal surfaces locked-in copy instead of a fresh approval CTA.
  lockedIn?: boolean;
  /** After intent-only approval, open checkout with the saved SKU. */
  onCompleteCheckout?: () => void | Promise<void>;
  /** Label for the secondary dismiss control on the success overlay. */
  successDismissLabel?: string;
  /** Floating adjust: recipient context (dashboard live path). */
  adjustRecipient?: DashboardConciergeDrawerRecipient | null;
  /** Signed-in user id — gates floating adjust when recipient is present. */
  adjustUserId?: string | null;
}

const TIER_LABEL = {
  standard: "Thoughtful",
  premium: "Impressive",
  luxury: "Unforgettable",
} as const;

function dollars(cents: number) {
  return `$${(cents / 100).toLocaleString("en-AU", { maximumFractionDigits: 0 })}`;
}

export function ApprovalModal({
  open,
  recommendation,
  onClose,
  onSend,
  onAdjust,
  onSkip,
  celebrationSkipped = false,
  onRevitalize,
  guestMode = false,
  onCreateAccount,
  intentOnly = false,
  lockedIn = false,
  onCompleteCheckout,
  successDismissLabel = "Back to up next",
  adjustRecipient = null,
  adjustUserId = null,
}: ApprovalModalProps) {
  const [success, setSuccess] = useState(false);
  // MS68: track in-flight + persist failure so the modal stops claiming
  // success when onSend throws. Previously setSuccess(true) fired
  // optimistically and the dashboard caught the error silently, so
  // the customer saw "It's on its way" even when the DB write failed.
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [revitalizing, setRevitalizing] = useState(false);
  const [revitalizeError, setRevitalizeError] = useState<string | null>(null);

  const secondaryBtnClass =
    "inline-flex items-center justify-center rounded-full border-2 border-navy/15 bg-white px-6 py-3 text-sm font-semibold text-navy transition-colors hover:border-coral hover:text-coral focus:outline-none focus:ring-2 focus:ring-coral/40";
  const primaryBtnClass =
    "inline-flex items-center justify-center rounded-full bg-coral px-6 py-3 text-sm font-semibold text-white shadow-[0_12px_28px_-10px_rgba(196,86,74,0.5)] transition-all hover:-translate-y-px hover:bg-coral-deep disabled:cursor-not-allowed disabled:opacity-60 disabled:hover:translate-y-0";

  const floatingAdjustEnabled = Boolean(adjustRecipient?.id && adjustUserId);
  const showAdjustControl =
    !lockedIn && !celebrationSkipped && (onAdjust != null || floatingAdjustEnabled);
  const showRevitalize =
    celebrationSkipped &&
    onRevitalize != null &&
    recommendation?.statusKey === "skipped";

  const { openForAdjust } = useFloatingConciergeUi();
  const profileQuery = useProfile();
  const teamMembersQuery = useTeamMembers(profileQuery.data?.company_id ?? null);
  const personalizationHints = pickPersonalizationHints(
    teamMembersQuery.data,
    adjustRecipient?.id,
  );

  // Lock body scroll while open so the dimmed dashboard stays put
  // behind the modal. Reset on close.
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
      // Defer to allow exit animation before resetting state
      const t = setTimeout(() => {
        setSuccess(false);
        setSendError(null);
        setSending(false);
        setRevitalizing(false);
        setRevitalizeError(null);
      }, 280);
      return () => clearTimeout(t);
    }
  }, [open]);

  // Esc closes the modal, matches the keyboard shortcut customers
  // expect from any in-app modal.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const handleAdjustClick = () => {
    if (floatingAdjustEnabled && adjustRecipient && recommendation) {
      openForAdjust(
        {
          recipientId: adjustRecipient.id,
          recipientName: adjustRecipient.name,
          occasion: adjustRecipient.occasion,
          celebrationId: adjustRecipient.celebrationId,
          celebrationDate: adjustRecipient.celebrationDate,
        },
        {
          productSummary: recommendation.productSummary,
          bakery: recommendation.bakery,
          totalCents: recommendation.totalCents,
        },
      );
      onClose();
      return;
    }
    if (recommendation) onAdjust?.(recommendation);
  };

  if (!recommendation) return null;

  // MS68: was setSuccess(true) optimistically then awaited onSend.
  // If the persist threw, the modal flipped to "It's on its way" anyway
  // and the user closed the modal trusting it had landed. Now we await
  // first and only flip success on resolve; failures show inline so the
  // user knows to retry. Sending state disables the button to prevent
  // double-fires while the DB write is in flight.
  const handleSend = async () => {
    if (sending || lockedIn) return;
    setSendError(null);
    setSending(true);
    try {
      const outcome = await onSend(recommendation);
      // onSend returns void when it neither redirected nor opened checkout;
      // any returned object means the flow handed off elsewhere — don't also
      // flip the in-modal success overlay.
      if (outcome) return;
      setSuccess(true);
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Couldn't send. Try once more?";
      setSendError(msg);
    } finally {
      setSending(false);
    }
  };

  const handleRevitalize = async () => {
    if (!onRevitalize || revitalizing) return;
    setRevitalizeError(null);
    setRevitalizing(true);
    try {
      await onRevitalize(recommendation);
    } catch (err) {
      const msg =
        err instanceof Error ? err.message : "Couldn't restore that celebration. Try once more?";
      setRevitalizeError(msg);
    } finally {
      setRevitalizing(false);
    }
  };

  const occasionLine =
    recommendation.daysOut === 0
      ? `${recommendation.recipientFirstName}'s ${recommendation.occasion} is today`
      : recommendation.daysOut === 1
      ? `${recommendation.recipientFirstName}'s ${recommendation.occasion} · tomorrow`
      : `${recommendation.recipientFirstName}'s ${recommendation.occasion} · ${recommendation.daysOut} days away`;

  return (
    <div
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      className={`fixed inset-0 z-[100] flex items-center justify-center p-8 transition-opacity duration-200 ${
        open ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
      }`}
      style={{
        backgroundColor: "rgba(29, 52, 97, 0.42)",
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
      }}
      aria-hidden={!open}
    >
      {/* MS202: was `max-h-[92vh]`. iOS Safari resolves `vh` against
          the address-bar-visible viewport, so the modal could exceed
          the actually-visible window. `dvh` tracks the live viewport
          — same fix as MS201 (landing hero, inline thread). The
          inner scroll body (a few lines below) gets the same update. */}
      <div
        className={`relative flex w-full max-h-[92dvh] flex-col overflow-hidden rounded-3xl shadow-2xl transition-transform duration-300 max-w-[920px] ${
          open ? "translate-y-0" : "translate-y-4"
        }`}
        style={{
          background: "var(--color-butter, #F7E8A4)",
          boxShadow: "0 32px 80px -16px rgba(29, 52, 97, 0.4)",
        }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="approval-modal-title"
      >
        <button
          onClick={onClose}
          className="absolute top-[18px] right-[18px] z-10 flex h-8 w-8 items-center justify-center rounded-full bg-white/50 text-navy backdrop-blur-md transition-colors hover:bg-white/85"
          aria-label="Close"
        >
          <X size={14} strokeWidth={2} />
        </button>

        <div className="relative flex-1 min-h-0 overflow-y-auto">
          {recommendation.imageUrl && <HeroPhoto imageUrl={recommendation.imageUrl} alt={recommendation.imageAlt ?? recommendation.productSummary} />}

          {/* Copy hero */}
          <div
            className="relative px-12 pt-11 pb-6"
            style={{
              backgroundImage:
                "radial-gradient(circle at top right, rgba(196,86,74,0.12) 0%, transparent 70%)",
            }}
          >
            <p
              className="mb-3 text-xs font-medium uppercase"
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                letterSpacing: "0.16em",
                color: "#C4564A",
              }}
            >
              {occasionLine}
            </p>
            <h2
              id="approval-modal-title"
              className="mb-3 text-navy"
              style={{
                fontFamily: "Newsreader, Georgia, serif",
                fontStyle: "italic",
                fontSize: "clamp(2rem, 4vw, 2.75rem)",
                lineHeight: 1.1,
                letterSpacing: "-0.01em",
                fontWeight: 500,
              }}
            >
              Here's what we'd send {recommendation.recipientFirstName} on {humanWeekday(recommendation.date)}.
            </h2>
            <p
              className="max-w-[620px] text-base leading-[1.65]"
              style={{ color: "rgba(29,52,97,0.92)" }}
            >
              {recommendation.productLine}
            </p>
            <PersonalizationHintsStrip
              {...personalizationHints}
              className="mt-4 max-w-[620px] rounded-xl border border-navy/10 bg-white/40 px-4 py-3"
            />
          </div>

          {/* Bits grid. MS68: first tile's emoji was hardcoded 🧁
              cupcake — wrong every time the engine picks a hamper /
              florist / spa / voucher. categoryEmoji() falls back to
              🎁 when vendorCategory is undefined (existing callers),
              and renders the right per-category icon once Dashboard
              starts passing vendor_category through. */}
          <div className="grid grid-cols-1 gap-3 px-12 pb-8 pt-4 md:grid-cols-2">
            <Bit
              emoji={categoryEmoji(recommendation.vendorCategory ?? "")}
              title={recommendation.productSummary}
              detail={`${recommendation.bakery} · ${recommendation.productLine.split(".")[0]}.`}
            />
            <Bit emoji="💌" title="A card from the team" detail={recommendation.cardDetail} />
            <Bit emoji="🚲" title="Delivered to your office" detail={recommendation.deliveryDetail} />
            <Bit emoji="🤝" title={`Why ${recommendation.bakery}`} detail={recommendation.rationaleDetail} />
          </div>

          {/* Action footer */}
          <div
            className="flex flex-wrap items-center gap-6 border-t px-12 py-7"
            style={{
              backgroundColor: "#FBF3D0",
              borderColor: "rgba(29,52,97,0.05)",
            }}
          >
            {showRevitalize ? (
              <>
                <button
                  type="button"
                  onClick={() => void handleRevitalize()}
                  disabled={revitalizing}
                  className={primaryBtnClass}
                  style={{ background: "#C4564A" }}
                >
                  {revitalizing
                    ? `Restoring ${recommendation.recipientFirstName}'s celebration…`
                    : `Bring ${recommendation.recipientFirstName} back to the schedule`}
                </button>
                {revitalizeError && (
                  <p role="alert" className="w-full text-xs" style={{ color: "#C4564A" }}>
                    {revitalizeError}
                  </p>
                )}
                <p
                  className="w-full text-xs leading-[1.5]"
                  style={{ color: "rgba(29,52,97,0.62)" }}
                >
                  You skipped this celebration. Restore it here to send, adjust, or skip again.
                </p>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => void handleSend()}
                  disabled={sending || lockedIn}
                  className={`${primaryBtnClass} gap-2`}
                  style={{ background: lockedIn ? "rgba(29,52,97,0.35)" : "#C4564A" }}
                >
                  {lockedIn
                    ? `Needs checkout for ${recommendation.recipientFirstName}`
                    : sending
                      ? `Sending ${recommendation.recipientFirstName}'s surprise…`
                      : `Send ${recommendation.recipientFirstName}'s surprise`}
                  {!lockedIn && <ArrowRight size={16} />}
                </button>
                {lockedIn && (
                  <p
                    role="status"
                    className="w-full text-xs"
                    style={{ color: "rgba(29,52,97,0.62)" }}
                  >
                    You've already approved this celebration. Head to Orders when you're ready to pay.
                  </p>
                )}
                {sendError && (
                  <p
                    role="alert"
                    className="w-full text-xs"
                    style={{ color: "#C4564A" }}
                  >
                    {sendError}. Please try again, nothing was charged.
                  </p>
                )}
                {(showAdjustControl || (onSkip && !lockedIn)) && (
                  <div className="flex flex-wrap gap-2">
                    {showAdjustControl && (
                      <button
                        type="button"
                        onClick={handleAdjustClick}
                        className={secondaryBtnClass}
                      >
                        Adjust something
                      </button>
                    )}
                    {onSkip && !lockedIn && (
                      <button
                        type="button"
                        onClick={() => onSkip(recommendation)}
                        className={secondaryBtnClass}
                      >
                        Skip this one
                      </button>
                    )}
                  </div>
                )}
              </>
            )}
            {/* MS68: AutoPilot copy gated on statusKey. Was rendered
                unconditionally — but ready/drafting recs are NOT auto-
                shipping, the user explicitly drives them via the Send
                button. Telling someone "AutoPilot ships as drafted
                unless you tap Skip" on a Ready rec is misleading. For
                non-autopilot recs we just describe the edit window
                without the auto-ship sentence. */}
            <p
              className="w-full border-t pt-3 text-xs leading-[1.5]"
              style={{ color: "rgba(29,52,97,0.4)", borderColor: "rgba(29,52,97,0.06)" }}
            >
              {/* MS76: when intentOnly=true (live authed dashboard),
                  we DON'T charge on tap — we save the approval intent
                  and follow up to confirm. Honest framing matches
                  reality. Demo + guest paths charge immediately so
                  keep the original copy. */}
              Total: <strong>{dollars(recommendation.totalCents)}</strong>
              {intentOnly
                ? " · we'll confirm timing + final price before charging."
                : " · you'll confirm delivery details before checkout."}{" "}
              {recommendation.statusKey === "autopilot" ? (
                <>
                  Edit window stays open until {recommendation.editWindowCloses}, after that, AutoPilot ships as drafted unless you tap Skip.{" "}
                </>
              ) : (
                <>Edit window stays open until {recommendation.editWindowCloses}.{" "}</>
              )}
              {TIER_LABEL[recommendation.tier]} tier.
            </p>
          </div>

          {/* Success overlay */}
          <div
            className={`absolute inset-0 flex flex-col items-center justify-center px-12 text-center transition-opacity duration-300 ${
              success ? "pointer-events-auto opacity-100" : "pointer-events-none opacity-0"
            }`}
            style={{ backgroundColor: "#F7E8A4" }}
          >
            <div
              className={`mb-6 flex h-[72px] w-[72px] items-center justify-center rounded-full text-white shadow-[0_12px_28px_-8px_rgba(196,86,74,0.5)] transition-transform duration-300 ${
                success ? "scale-100" : "scale-50"
              }`}
              style={{ background: "#C4564A" }}
            >
              <Check size={36} strokeWidth={2.5} />
            </div>
            {/* MS76: success-state copy now honest about what just
                happened. The live authed dashboard (intentOnly=true)
                only saves the approval intent — no order created, no
                payment taken — so "It's on its way" was a falsehood
                the customer would discover later. Demo + guest checkout
                still see the optimistic copy because in those flows
                the order/charge actually happens. */}
            <h3
              className="mb-3 text-navy"
              style={{
                fontFamily: "Newsreader, Georgia, serif",
                fontStyle: "italic",
                fontSize: "clamp(2rem, 4vw, 2.5rem)",
                lineHeight: 1.1,
                letterSpacing: "-0.01em",
                fontWeight: 500,
              }}
            >
              {intentOnly
                ? `Saved for ${recommendation.recipientFirstName}.`
                : `It's on its way to ${recommendation.recipientFirstName}.`}
            </h3>
            <p className="mb-7 max-w-[440px] text-base leading-[1.6]" style={{ color: "rgba(29,52,97,0.92)" }}>
              {intentOnly ? (
                onCompleteCheckout ? (
                  <>
                    Your gift for {recommendation.recipientFirstName} is locked in.
                    Complete checkout now while everything is fresh, or come back
                    from Home when you are ready.
                  </>
                ) : (
                  <>
                    We've logged your approval. Lembra will firm up the timing with
                    {" "}{recommendation.bakery}{" "}
                    and reach out to confirm before charging your card.
                  </>
                )
              ) : (
                <>
                  {recommendation.bakery} will deliver at <strong>{recommendation.deliveryDetail}</strong>.
                  {guestMode
                    ? ` We'll email you the receipt and a tracking note when it ships.`
                    : ` A personalised card comes with the delivery. We'll send you a heads-up when it ships.`}
                </>
              )}
            </p>
            {intentOnly && onCompleteCheckout ? (
              <div className="flex flex-col items-center gap-3 sm:flex-row">
                <button
                  type="button"
                  onClick={() => void onCompleteCheckout()}
                  className="inline-flex items-center gap-2 rounded-full px-7 py-3 text-sm font-semibold transition-colors hover:bg-coral/85"
                  style={{ background: "#C4564A", color: "#FFF8E7" }}
                >
                  Complete checkout
                  <ArrowRight size={16} />
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-full border-2 border-navy/15 bg-white px-6 py-3 text-sm font-semibold text-navy transition-colors hover:border-coral hover:text-coral"
                >
                  {successDismissLabel}
                </button>
              </div>
            ) : (
              <button
                onClick={onClose}
                className="rounded-full px-7 py-3 text-sm font-semibold transition-colors hover:bg-[#14253D]"
                style={{ background: "#1D3461", color: "#FFF8E7" }}
              >
                {guestMode ? "Done" : successDismissLabel}
              </button>
            )}
            {guestMode && onCreateAccount && (
              <button
                onClick={onCreateAccount}
                className="mt-5 text-[13.5px] underline-offset-[3px] hover:underline"
                style={{
                  color: "rgba(29,52,97,0.62)",
                  fontFamily: "Newsreader, Georgia, serif",
                  fontStyle: "italic",
                }}
              >
                Want every birthday and anniversary handled like this? <span style={{ color: "#C4564A", fontWeight: 500 }}>Set up your team →</span>
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// HeroPhoto: renders the actual product image at the top of the
// modal. If the URL fails (CDN timeout, photo ID changed), shows a
// styled fallback that still looks designed, the customer never
// sees a broken-image icon. Once the engine is wired and image_url
// comes from bakery_products, the fallback can stay as a backstop
// for bakeries that haven't uploaded photos yet.
function HeroPhoto({ imageUrl, alt }: { imageUrl: string; alt: string }) {
  const [failed, setFailed] = useState(false);
  return (
    <div
      className="relative w-full overflow-hidden"
      style={{ aspectRatio: "16 / 7", background: "linear-gradient(135deg, #F4D8A4 0%, #E8B5B0 60%, #F2DD8C 100%)" }}
    >
      {!failed ? (
        <img
          src={imageUrl}
          alt={alt}
          loading="eager"
          onError={() => setFailed(true)}
          className="absolute inset-0 h-full w-full object-cover"
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center">
          <span
            className="rounded-full bg-white/85 px-4 py-2 text-[12px] font-semibold backdrop-blur"
            style={{ color: "#1D3461", fontFamily: "JetBrains Mono, monospace", letterSpacing: "0.04em" }}
          >
            Product photo
          </span>
        </div>
      )}
      {/* Soft inner shadow to bleed the photo into the butter modal */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 bottom-0 h-16"
        style={{ background: "linear-gradient(to bottom, transparent 0%, rgba(247,232,164,0.85) 100%)" }}
      />
    </div>
  );
}

function Bit({ emoji, title, detail }: { emoji: string; title: string; detail: string }) {
  return (
    <div
      className="flex items-start gap-3 rounded-2xl p-4 backdrop-blur"
      style={{ background: "rgba(255,255,255,0.7)" }}
    >
      <div
        className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-lg"
        style={{ background: "rgba(196,86,74,0.12)" }}
      >
        {emoji}
      </div>
      <div>
        <div className="mb-1 text-sm font-semibold text-navy">{title}</div>
        <div className="text-[12.5px] leading-[1.5]" style={{ color: "rgba(29,52,97,0.55)" }}>
          {detail}
        </div>
      </div>
    </div>
  );
}

function humanWeekday(iso: string) {
  // "2026-05-15" → "Thursday"
  const d = new Date(iso + "T12:00:00"); // anchor mid-day to avoid TZ drift
  return d.toLocaleDateString("en-AU", { weekday: "long" });
}
